import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import {
  JOB_DESTINATION, appLink, claimInvite, forgetInvite, formatDeadline, inviteFrom, loadInvitePreview,
  rememberInvite, signInPath, signUpPath,
} from './outreach.js';
import { StatusState } from '../Editorial.jsx';

/** /join?invite=<token>: a business we invited by email lands here to join and quote. */
export function JoinPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const token = inviteFrom(location.search);
  const [preview, setPreview] = useState(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    if (!token) { setPreview(null); return undefined; }
    rememberInvite(token);
    loadInvitePreview(supabase, token)
      .then((value) => { if (live) setPreview(value || null); })
      .catch((cause) => { if (live) { setPreview(null); setError(cause.message); } });
    return () => { live = false; };
  }, [token]);

  const openJob = async () => {
    setBusy(true); setError('');
    try { await claimInvite(supabase, token); forgetInvite(); navigate(JOB_DESTINATION, { replace: true }); }
    catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  };

  if (!token) {
    return <div className="rs-container rs-state-page">
      <StatusState icon="mailWarning" kicker="SUPPLIER INVITATION" title="This invitation link is not complete." wide>
        <div className="rs-state-row"><p>Please open the link from your email again, or create a free supplier account.</p><Link className="rs-button is-large" to="/sign-up?workspace=supplier">Create a free supplier account</Link></div>
      </StatusState>
    </div>;
  }
  if (preview === undefined || loading) return <div className="rs-container rs-page"><p>Loading the job…</p></div>;

  return <div className="rs-container rs-page join-page">
    <header className="rs-page-head">
      <p className="rs-kicker">INVITATION TO QUOTE</p>
      <h1 className="rs-title">{preview?.title ? 'A local job that matches your trade.' : 'Join Resident Service 360 to quote for local jobs.'}</h1>
      <p className="rs-lede">Resident Service 360 is free for tradespeople to join. Customers post jobs, you send a quote, and the customer chooses who does the work.</p>
    </header>
    {preview?.title ? <section className="account-content-panel" aria-label="Job">
      <h2>{preview.title}</h2>
      <dl className="mod-fields">
        {preview.trade && <><dt>Trade</dt><dd>{preview.trade}</dd></>}
        {preview.area && <><dt>Area</dt><dd>{preview.area}</dd></>}
        {preview.deadline && <><dt>Quotes close</dt><dd>{formatDeadline(preview.deadline)}</dd></>}
      </dl>
      {!preview.open && <p className="mod-note">This job is no longer taking quotes, but you can still join to see other local jobs.</p>}
    </section> : <p className="mod-note">We could not find this job. It may have been removed. You can still join to see other local jobs.</p>}
    {error && <p className="account-form-error" role="alert">{error}</p>}
    <div className="mod-action-row" style={{ marginTop: 20 }}>
      {user
        ? <button type="button" className="button button-blue" onClick={openJob} disabled={busy}>{busy ? 'Opening…' : 'See local jobs and quote'}</button>
        : <>
          <Link className="button button-blue" to={signUpPath(token)}>Quote for free: create a supplier account</Link>
          <Link className="button button-secondary" to={signInPath(token)}>I already have an account</Link>
        </>}
      <a className="text-link" href={appLink(token)}>Open in the Resident Service 360 app</a>
    </div>
    <p className="mod-note" style={{ marginTop: 20 }}>You got this link because we invited your business by email. Not interested? Use the unsubscribe link in that email and we will not contact you again.</p>
  </div>;
}

export default JoinPage;
